import { useQuery } from '@tanstack/react-query';
import { useApplyTenantTheme } from '@mlm/ui/lib/tenant-theme';
import { getTenantConfig, tenantLogoUrl } from './api';
import { hostLogoOverride } from './host-logo';
import { useThemeMode } from './theme-mode';

/**
 * The member app's half of tenant theming: resolve WHICH theme, WHICH mode and
 * WHICH favicon, then hand them to the shared applier in `@mlm/ui`
 * (`useApplyTenantTheme`), which owns the DOM work and is shared with the
 * creator portal.
 *
 * Reuses the shared `['tenant-config']` query (dedup'd with the rest of the
 * app), so this adds no extra network request.
 *
 * NOTE: this themes surfaces that read `:root` vars (member portal shell,
 * admin, cashier). The public storefront defines its own vars scoped under
 * `.omni-store` — a `:root` override does not reach them UNLESS the tenant
 * declares surface tokens of its own, which puts a `tenant-surfaces` class on
 * <html> and switches that scoped block off (see `storefront.css`).
 */
export function useTenantTheme() {
  const { data } = useQuery({
    queryKey: ['tenant-config'],
    queryFn: getTenantConfig,
    retry: 0,
  });
  const theme = data?.theme ?? null;
  const mode = useThemeMode((s) => s.mode);
  // A stored 'dark' from another deployment (or a theme that dropped its dark
  // set) must not strand the portal in a half-dark state: no dark tokens, no
  // dark mode.
  const effectiveMode = theme?.dark ? mode : 'light';

  // White-label favicon: a per-hostname override (multi-domain tenants) wins,
  // then a dedicated uploaded favicon, then the uploaded logo, then a pasted
  // logo URL (falls back to the static index.html /favicon.svg when none is
  // set). The index.html baked favicon is host-agnostic, so on an overridden
  // host the tab icon swaps after boot (brief, acceptable).
  const faviconUrl =
    hostLogoOverride() ??
    (theme?.faviconKey
      ? `${tenantLogoUrl('favicon')}?v=${encodeURIComponent(theme.faviconKey)}`
      : theme?.logoKey
        ? `${tenantLogoUrl('light')}?v=${encodeURIComponent(theme.logoKey)}`
        : (theme?.logoUrl ?? null));

  useApplyTenantTheme({ theme, mode: effectiveMode, faviconUrl });
}
